import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnNotificationGroup, SnNotificationGroupType } from '@lib/api/entities';
import { t } from '@lib/translation/helpers/translation.helper';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnNotificationGroupsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public loadNotificationGroups(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnNotificationGroup>> {
    return this._httpClient.get('/api/notification-groups/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((team) => new SnNotificationGroup(team)),
      })),
    );
  }

  public feedNotificationGroups(params: ISnFeedQueryParameters): Observable<SnNotificationGroup[]> {
    return this._httpClient
      .get('/api/notification-groups/', { params })
      .pipe(map((response: any) => response.map((item: Object) => new SnNotificationGroup(item))));
  }

  public getNotificationGroup(id: string): Observable<SnNotificationGroup> {
    return this._httpClient
      .get(`/api/notification-groups/${id}/`)
      .pipe(map((response: Object) => new SnNotificationGroup(response)));
  }

  public getTypeNotificationGroup(): Observable<ISnListResponse<SnNotificationGroupType>> {
    return this._httpClient.get('/api/notification-groups/types/').pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((type) => new SnNotificationGroupType(type)),
      })),
    );
  }

  public createNotificationGroup(data: any): Observable<SnNotificationGroup> {
    return this._httpClient.post('/api/notification-groups/', data).pipe(
      map((response: SnNotificationGroup) => new SnNotificationGroup(response)),
      tap({
        next: () => this._toastrService.success(t('settings.notificationGroups.created')),
        error: () => this._toastrService.error(t('settings.notificationGroups.createError')),
      }),
    );
  }

  public updateNotificationGroup(id: string, data: any): Observable<SnNotificationGroup> {
    return this._httpClient.patch(`/api/notification-groups/${id}/`, data).pipe(
      map((response: Object) => new SnNotificationGroup(response)),
      tap({
        next: () => this._toastrService.success(t('settings.notificationGroups.updated')),
        error: () => this._toastrService.error(t('settings.notificationGroups.updateError')),
      }),
    );
  }

  public deleteNotificationGroup(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('settings.notificationGroups.deleteConfirm'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/notification-groups/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('settings.notificationGroups.deleted')),
          error: () => this._toastrService.error(t('settings.notificationGroups.deleteError')),
        }),
      );
  }
}
